import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { gapi, errMsg, fmtDateTime } from "../api";
import { useGAuth } from "../GAuth";
import { PageHeader, Panel, Btn, Badge, inputCls } from "../components/ui";

function InviteDialog({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ email: "", nome: "", ruolo: "agente" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await gapi.post("/invites", f); setRes(data); onDone(); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setLoading(false); }
  };
  const close = (v) => { onOpenChange(v); if (!v) { setRes(null); setF({ email: "", nome: "", ruolo: "agente" }); } };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="g-invite-dialog">
        <DialogHeader><DialogTitle>Invita un collaboratore</DialogTitle><DialogDescription>Il link è personale, scade dopo 72 ore e può essere usato una sola volta.</DialogDescription></DialogHeader>
        {res ? (
          <div className="space-y-3" data-testid="g-invite-result">
            <Badge tone={res.emailInviata ? "green" : "amber"}>{res.emailInviata ? "Email di invito inviata" : "Email non inviata: copia il link e invialo tu"}</Badge>
            <div className="flex gap-2"><input readOnly value={res.link} className={inputCls} data-testid="g-invite-link" />
              <Btn variant="ghost" onClick={() => { navigator.clipboard?.writeText(res.link); toast.success("Link copiato"); }}><Copy className="h-4 w-4" /></Btn></div>
            <p className="text-xs text-anthracite/50">Scade il {fmtDateTime(res.scadeIl)}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input className={inputCls} type="email" placeholder="Email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="g-invite-email" />
            <input className={inputCls} placeholder="Nome e cognome (facoltativo)" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="g-invite-name" />
            <select className={inputCls} value={f.ruolo} onChange={(e) => setF({ ...f, ruolo: e.target.value })} data-testid="g-invite-role">
              <option value="agente">Agente / Collaboratore</option><option value="segreteria">Segreteria</option>
            </select>
            <Btn type="submit" disabled={loading} className="w-full" data-testid="g-invite-send">Crea invito</Btn>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SessionsDialog({ target, onClose }) {
  const [list, setList] = useState(null);
  const load = useCallback(() => target && gapi.get(`/users/${target.id}/sessions`).then((r) => setList(r.data)), [target]);
  useEffect(() => { setList(null); load(); }, [load]);
  const revoke = async () => {
    try { const { data } = await gapi.post(`/users/${target.id}/sessions/revoke`); toast.success(`${data.revocate} sessioni revocate`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="g-sessions-dialog">
        <DialogHeader><DialogTitle>Sessioni attive · {target?.nome}</DialogTitle><DialogDescription>Revocando le sessioni l'utente dovrà accedere di nuovo.</DialogDescription></DialogHeader>
        {!list ? <p className="text-sm">Caricamento…</p> : !list.length ? <p className="text-sm text-anthracite/50">Nessuna sessione attiva.</p> : (
          <ul className="text-sm divide-y divide-border-subtle">{list.map((s) => <li key={s.id} className="py-2"><b>{fmtDateTime(s.creataIl)}</b> · {s.ip}<br /><span className="text-xs text-anthracite/45">{s.userAgent}</span></li>)}</ul>
        )}
        <Btn variant="danger" onClick={revoke} data-testid="g-sessions-revoke">Revoca tutte le sessioni</Btn>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDialog({ target, users, onClose }) {
  const [counts, setCounts] = useState(null);
  const [dest, setDest] = useState("");
  useEffect(() => { if (target) { setCounts(null); setDest(""); gapi.get(`/users/${target.id}/assignments`).then((r) => setCounts(r.data)); } }, [target]);
  const run = async () => {
    try { const { data } = await gapi.post(`/users/${target.id}/reassign`, { versoUserId: dest }); const n = Object.values(data.risultato).reduce((a, b) => a + b, 0); toast.success(`${n} elementi riassegnati`); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const total = (counts || []).reduce((a, c) => a + c.numero, 0);
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="g-reassign-dialog">
        <DialogHeader><DialogTitle>Riassegna il lavoro di {target?.nome}</DialogTitle><DialogDescription>Contatti, immobili, attività e altri elementi passeranno al collaboratore scelto. La cronologia resta attribuita all'autore originale.</DialogDescription></DialogHeader>
        {!counts ? <p className="text-sm">Caricamento…</p> : (
          <ul className="grid grid-cols-2 gap-1 text-sm">{counts.map((c) => <li key={c.collezione} className="flex justify-between rounded-lg bg-accent-subtle px-3 py-1.5"><span>{c.label}</span><b>{c.numero}</b></li>)}</ul>
        )}
        <select className={inputCls} value={dest} onChange={(e) => setDest(e.target.value)} data-testid="g-reassign-dest">
          <option value="">Scegli il destinatario…</option>
          {users.filter((u) => u.attivo && u.id !== target?.id).map((u) => <option key={u.id} value={u.id}>{`${u.nome} (${u.ruolo})`}</option>)}
        </select>
        <Btn onClick={run} disabled={!dest} data-testid="g-reassign-confirm">Riassegna {total} elementi</Btn>
      </DialogContent>
    </Dialog>
  );
}

const INV_TONE = { "in attesa": "blue", accettato: "green", scaduto: "neutral", revocato: "red" };

export default function Utenti() {
  const { session } = useGAuth();
  const isOwner = session.user.ruolo === "titolare";
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [sessFor, setSessFor] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);

  const load = useCallback(() => {
    gapi.get("/users").then((r) => setUsers(r.data));
    gapi.get("/invites").then((r) => setInvites(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="g-users-page">
      <PageHeader eyebrow="Amministrazione" title="Utenti e inviti" desc="Solo le persone invitate dal titolare possono accedere. Un utente disattivato perde subito l'accesso; le sue operazioni restano nella cronologia."
        actions={isOwner && <Btn onClick={() => setInviteOpen(true)} data-testid="g-invite-open"><UserPlus className="h-4 w-4" /> Invita collaboratore</Btn>} />
      <Panel title="Utenti" className="mb-6">
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-anthracite/45"><th className="py-2 px-5 sm:px-2">Nome</th><th className="px-2">Ruolo</th><th className="px-2">Stato</th><th className="px-2">Ultimo accesso</th><th className="px-2 text-right">Azioni</th></tr></thead>
            <tbody className="divide-y divide-border-subtle">
              {users.map((u) => (
                <tr key={u.id} data-testid={`g-user-row-${u.email}`}>
                  <td className="py-3 px-5 sm:px-2"><b>{u.nome}</b><br /><span className="text-xs text-anthracite/50">{u.email}</span></td>
                  <td className="px-2">{u.ruolo === "titolare" || !isOwner ? session.ruoli[u.ruolo] : (
                    <select value={u.ruolo} onChange={(e) => act(() => gapi.patch(`/users/${u.id}`, { ruolo: e.target.value }), "Ruolo aggiornato")} className="h-9 rounded-lg border border-border-subtle px-2" data-testid={`g-user-role-${u.email}`}>
                      <option value="agente">Agente / Collaboratore</option><option value="segreteria">Segreteria</option>
                    </select>)}</td>
                  <td className="px-2"><Badge tone={u.attivo ? "green" : "red"} testid={`g-user-status-${u.email}`}>{u.attivo ? "Attivo" : `Disattivato ${u.disattivatoIl ? "il " + fmtDateTime(u.disattivatoIl) : ""}`}</Badge></td>
                  <td className="px-2 text-anthracite/60">{fmtDateTime(u.ultimoAccesso)}</td>
                  <td className="px-2 text-right whitespace-nowrap space-x-1">
                    {isOwner && <Btn variant="ghost" className="h-8 px-3 text-xs" onClick={() => setSessFor(u)} data-testid={`g-user-sessions-${u.email}`}>Sessioni</Btn>}
                    {isOwner && u.ruolo !== "titolare" && <Btn variant="ghost" className="h-8 px-3 text-xs" onClick={() => setReassignFor(u)} data-testid={`g-user-reassign-${u.email}`}>Riassegna</Btn>}
                    {isOwner && u.ruolo !== "titolare" && (u.attivo
                      ? <Btn variant="danger" className="h-8 px-3 text-xs" onClick={() => window.confirm(`Disattivare ${u.nome}? Perderà subito l'accesso.`) && act(() => gapi.post(`/users/${u.id}/deactivate`), "Utente disattivato e sessioni revocate")} data-testid={`g-user-deactivate-${u.email}`}>Disattiva</Btn>
                      : <Btn variant="ghost" className="h-8 px-3 text-xs" onClick={() => act(() => gapi.post(`/users/${u.id}/reactivate`), "Utente riattivato")} data-testid={`g-user-reactivate-${u.email}`}>Riattiva</Btn>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Inviti">
        {!invites.length ? <p className="text-sm text-anthracite/45">Nessun invito creato.</p> : (
          <ul className="divide-y divide-border-subtle text-sm">
            {invites.map((i) => (
              <li key={i.id} className="py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`g-invite-row-${i.email}`}>
                <span><b>{i.email}</b> · {session.ruoli[i.ruolo]}<br /><span className="text-xs text-anthracite/50">Creato il {fmtDateTime(i.createdAt)} da {i.creatoDaNome} · scade {fmtDateTime(i.scadeIl)}</span></span>
                <span className="flex items-center gap-2"><Badge tone={INV_TONE[i.stato]}>{i.stato}</Badge>
                  {isOwner && i.stato === "in attesa" && <Btn variant="danger" className="h-8 px-3 text-xs" onClick={() => act(() => gapi.post(`/invites/${i.id}/revoke`), "Invito revocato")} data-testid={`g-invite-revoke-${i.email}`}>Revoca</Btn>}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onDone={load} />
      <SessionsDialog target={sessFor} onClose={() => setSessFor(null)} />
      <ReassignDialog target={reassignFor} users={users} onClose={() => setReassignFor(null)} />
    </div>
  );
}
